import React from "react";
import { getActiveInputHandler } from "../../engine/input/InputHandler.js";
import { EventBus } from "../../engine/EventBus.js";
import { useLayout } from "../hooks/useLayout.js";

/**
 * U05 — on-screen nav strip for touch modes.
 *
 * Dispatches the same grid-movement commands the keyboard handler does,
 * via `getActiveInputHandler()`. Hidden on desktop (handled by the CSS
 * `data-layout-mode` selector — callers mount this unconditionally
 * inside mobile branches of `App.tsx`).
 *
 * Layout: horizontal 5-button strip pinned to the bottom of the FPV.
 * Forward sits in the middle, turns flank it, and strafes bracket the
 * outside. Backward is dropped — very rarely needed and the player can
 * always turn-around-then-move. No on-screen back button frees up
 * vertical space for the FPV itself.
 *
 *   ⟵   ⟲   ▲   ⟳   ⟶
 *  strL  tL  fwd  tR  strR
 */
export function NavCluster(): React.ReactElement {
  const layout = useLayout();
  // In mobile-portrait the viewport is split: FPV is boxed into the top
  // 56vh, HUD dock fills the bottom. Anchor the nav strip to the top of
  // the dock (just under the FPV edge) so it sits above the PartyBar.
  // In mobile-landscape keep the strip pinned to the bottom of the
  // viewport where it's closest to the player's thumbs.
  const anchor: React.CSSProperties =
    layout === "mobile-portrait"
      ? { top: "calc(50dvh + 8px)" }
      : { bottom: "calc(12px + env(safe-area-inset-bottom, 0px))" };

  const send = {
    forward: () => {
      getActiveInputHandler()?.commandMove("forward");
    },
    strafeLeft: () => {
      getActiveInputHandler()?.commandStrafe("left");
    },
    strafeRight: () => {
      getActiveInputHandler()?.commandStrafe("right");
    },
    turnLeft: () => {
      getActiveInputHandler()?.commandTurn("left");
    },
    turnRight: () => {
      getActiveInputHandler()?.commandTurn("right");
    },
  };

  return (
    <div
      data-component="nav-cluster"
      style={{
        position: "absolute",
        left: "50%",
        ...anchor,
        transform: "translateX(-50%)",
        display: "grid",
        gridTemplateColumns: "repeat(7, 56px)",
        gridTemplateRows: "56px",
        gap: 4,
        pointerEvents: "auto",
        userSelect: "none",
        touchAction: "none",
        zIndex: 40,
      }}
    >
      <Button
        label={<PotionGlyph />}
        title="Toggle consumables"
        onPress={() => EventBus.emit("ui:toggle_consumables", {})}
      />
      <Button label="⟵" title="Strafe left" onPress={send.strafeLeft} />
      <Button label="⟲" title="Turn left" onPress={send.turnLeft} />
      <Button label="▲" title="Forward" onPress={send.forward} />
      <Button label="⟳" title="Turn right" onPress={send.turnRight} />
      <Button label="⟶" title="Strafe right" onPress={send.strafeRight} />
      <Button label="" title="" disabled />
    </div>
  );
}

function PotionGlyph(): React.ReactElement {
  return (
    <svg
      width="26"
      height="26"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M9 3h6" />
      <path d="M10 3v4.5" />
      <path d="M14 3v4.5" />
      <path d="M7 14a5 5 0 0 0 10 0c0-2-1.5-3.5-3-6.5h-4C8.5 10.5 7 12 7 14z" />
      <path d="M9 14h6" />
    </svg>
  );
}

function Button({
  label,
  title,
  onPress,
  disabled,
}: {
  label: React.ReactNode;
  title: string;
  onPress?: () => void;
  disabled?: boolean;
}): React.ReactElement {
  // `onPointerDown` fires before the native touchend-delay, so the nav
  // feels as snappy as the keyboard. `preventDefault` stops the
  // synthetic click + dedicated double-tap-zoom fallback.
  const handle = (e: React.PointerEvent<HTMLButtonElement>) => {
    e.preventDefault();
    onPress?.();
  };
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      disabled={disabled}
      onPointerDown={disabled ? undefined : handle}
      style={{
        width: "100%",
        height: "100%",
        background: disabled ? "rgba(0,0,0,0.25)" : "rgba(0,0,0,0.55)",
        color: disabled ? "rgba(255,255,255,0.25)" : "#fff",
        border: disabled
          ? "1px dashed rgba(255,255,255,0.15)"
          : "1px solid rgba(255,255,255,0.3)",
        borderRadius: 8,
        fontFamily: "monospace",
        fontSize: 22,
        lineHeight: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: disabled ? "default" : "pointer",
        touchAction: "none",
      }}
    >
      {label}
    </button>
  );
}
